import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { TOKENS } from "../tokens";
import { LogOut, MapPin, Download, AlertCircle } from "lucide-react";
import api from "../api/client";

export function SuperAdminApp() {
  const { logout } = useApp();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/admin/team-locations");
      setLocations(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem("tracker_token");
    const baseURL = api.defaults.baseURL;
    window.location.href = `${baseURL}/admin/export-locations?token=${token}`;
  };

  return (
    <div style={{ minHeight: "100vh", background: TOKENS.bgPage, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <header style={{ background: TOKENS.navyDeep, color: "#fff", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <MapPin size={24} color={TOKENS.gold} />
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Super Admin Dashboard</h1>
        </div>
        <button 
          onClick={logout}
          style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}
        >
          <LogOut size={16} /> Logout
        </button>
      </header>

      {/* Main Content */}
      <main style={{ flex: 1, padding: 32, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 24, color: TOKENS.navyDeep }}>Team Locations (Today)</h2>
          <button 
            onClick={handleExport}
            style={{ background: TOKENS.gold, color: TOKENS.navyDeep, border: "none", padding: "10px 20px", borderRadius: 8, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
          >
            <Download size={18} /> Export to Excel
          </button>
        </div>

        {error && (
          <div style={{ background: TOKENS.dangerBg, color: TOKENS.danger, padding: 16, borderRadius: 12, marginBottom: 24, display: "flex", alignItems: "center", gap: 12, fontWeight: 600 }}>
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: TOKENS.muted, fontWeight: 500 }}>
            Loading locations...
          </div>
        ) : locations.length > 0 ? (
          <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.04)", border: `1px solid ${TOKENS.border}` }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: TOKENS.cream, borderBottom: `1px solid ${TOKENS.border}` }}>
                  <th style={{ padding: "16px 24px", color: TOKENS.navyDeep, fontWeight: 600, fontSize: 14 }}>Emp ID</th>
                  <th style={{ padding: "16px 24px", color: TOKENS.navyDeep, fontWeight: 600, fontSize: 14 }}>Name</th>
                  <th style={{ padding: "16px 24px", color: TOKENS.navyDeep, fontWeight: 600, fontSize: 14 }}>Status</th>
                  <th style={{ padding: "16px 24px", color: TOKENS.navyDeep, fontWeight: 600, fontSize: 14 }}>GPS</th>
                  <th style={{ padding: "16px 24px", color: TOKENS.navyDeep, fontWeight: 600, fontSize: 14 }}>Location / Coordinates</th>
                  <th style={{ padding: "16px 24px", color: TOKENS.navyDeep, fontWeight: 600, fontSize: 14 }}>Last Update</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((loc, idx) => (
                  <tr key={loc.id} style={{ borderBottom: idx < locations.length - 1 ? `1px solid ${TOKENS.border}` : "none" }}>
                    <td style={{ padding: "16px 24px", fontSize: 14, fontWeight: 600, color: TOKENS.navyDeep }}>{loc.employeeId}</td>
                    <td style={{ padding: "16px 24px", fontSize: 14, color: TOKENS.ink }}>{loc.name}</td>
                    <td style={{ padding: "16px 24px" }}>
                      <span style={{ background: loc.checkedIn ? TOKENS.successBg : TOKENS.mutedBg, color: loc.checkedIn ? TOKENS.success : TOKENS.muted, padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                        {loc.checkedIn ? "Checked In" : "Checked Out"}
                      </span>
                    </td>
                    <td style={{ padding: "16px 24px" }}>
                      <span style={{ background: loc.gpsStatus === "ON" ? TOKENS.successBg : TOKENS.dangerBg, color: loc.gpsStatus === "ON" ? TOKENS.success : TOKENS.danger, padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                        {loc.gpsStatus}
                      </span>
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: 14, color: TOKENS.ink }}>
                      <div style={{ fontWeight: 500 }}>{loc.locationText}</div>
                      {loc.lat && loc.lng && (
                        <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 4 }}>
                          {loc.lat}, {loc.lng}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "16px 24px", fontSize: 14, color: TOKENS.muted }}>
                      {loc.lastUpdate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !error && (
            <div style={{ padding: 40, textAlign: "center", color: TOKENS.muted, fontWeight: 500, background: "#fff", borderRadius: 16, border: `1px solid ${TOKENS.border}` }}>
              No team locations found.
            </div>
          )
        )}
      </main>
    </div>
  );
}
